/**
 * AI Chatbot Widget
 *
 * Floating chatbot interface for AI assistance.
 *
 * @since 2.6.0
 */

/* global democraticChatbot */

(function ($) {
	'use strict';

	// Chatbot state
	let isOpen = false;
	let conversationHistory = [];
	let displayMessages = [];
	const HISTORY_TTL_MS = 24 * 60 * 60 * 1000;
	const MAX_STORED_TURNS = 40;

	function storageKey() {
		const host =
			(window.location && window.location.host) ||
			(democraticChatbot && democraticChatbot.storageHost) ||
			'default';
		return `democratic_ai_chat_v1_${host}`;
	}

	function persistConversation() {
		try {
			const payload = {
				conversationHistory: conversationHistory.slice(-MAX_STORED_TURNS),
				displayMessages: displayMessages.slice(-(MAX_STORED_TURNS * 2 + 2)),
				isOpen,
				updatedAt: Date.now(),
			};
			window.localStorage.setItem(storageKey(), JSON.stringify(payload));
		} catch (e) {
			// Ignore quota / private mode failures.
		}
	}

	function loadPersistedConversation() {
		try {
			const raw = window.localStorage.getItem(storageKey());
			if (!raw) {
				return null;
			}
			const parsed = JSON.parse(raw);
			if (!parsed || !parsed.updatedAt || Date.now() - parsed.updatedAt > HISTORY_TTL_MS) {
				window.localStorage.removeItem(storageKey());
				return null;
			}
			return parsed;
		} catch (e) {
			return null;
		}
	}

	function clearPersistedConversation() {
		try {
			window.localStorage.removeItem(storageKey());
		} catch (e) {
			// Ignore.
		}
		conversationHistory = [];
		displayMessages = [];
		$('#chatbot-messages').empty();
	}

	/**
	 * Initialize chatbot
	 */
	function initChatbot() {
		// Check if chatbot config is available
		if (typeof democraticChatbot === 'undefined') {
			// Chatbot not configured, exit silently
			return;
		}

		const clearLabel =
			(democraticChatbot.i18n && democraticChatbot.i18n.clear) || 'Clear chat';

		// Create chatbot HTML
		const chatbotHtml = `
			<div id="democratic-chatbot-container" class="chatbot-container">
				<!-- Floating Button -->
				<button type="button" id="chatbot-toggle" class="chatbot-toggle text-white p-0" aria-label="${democraticChatbot.i18n.title}">
					<svg class="chatbot-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
					</svg>
					<svg class="chatbot-close-icon text-white" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
					</svg>
				</button>

				<!-- Chat Window -->
				<div id="chatbot-window" class="chatbot-window">
					<div class="chatbot-header">
						<h3>${democraticChatbot.i18n.title}</h3>
						<div class="chatbot-header-actions">
							<button type="button" class="chatbot-clear p-0" aria-label="${clearLabel}" title="${clearLabel}">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8"/>
								</svg>
							</button>
							<button type="button" class="chatbot-minimize p-0" aria-label="${democraticChatbot.i18n.minimize}">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
								</svg>
							</button>
							<button type="button" class="chatbot-close p-0" aria-label="${democraticChatbot.i18n.close}">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
								</svg>
							</button>
						</div>
					</div>
					<div class="chatbot-messages" id="chatbot-messages"></div>
					<div class="chatbot-input-container">
						<label for="chatbot-input" class="sr-only">${democraticChatbot.i18n.placeholder}</label>
						<textarea 
							id="chatbot-input" 
							class="chatbot-input" 
							placeholder="${democraticChatbot.i18n.placeholder}"
							rows="1"
							aria-label="${democraticChatbot.i18n.placeholder}"
						></textarea>
						<button type="button" id="chatbot-send" class="chatbot-send p-0" aria-label="${democraticChatbot.i18n.send}">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" aria-hidden="true">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		`;

		// Append to body
		$('body').append(chatbotHtml);

		// Attach event handlers
		attachEventHandlers();

		const saved = loadPersistedConversation();
		if (
			saved &&
			Array.isArray(saved.displayMessages) &&
			saved.displayMessages.length > 0
		) {
			conversationHistory = Array.isArray(saved.conversationHistory)
				? saved.conversationHistory
				: [];
			displayMessages = saved.displayMessages.slice();
			saved.displayMessages.forEach((entry) => {
				if (!entry || !entry.text) {
					return;
				}
				addMessage(entry.text, entry.sender || 'bot', !!entry.isError, false);
				if (
					entry.sender === 'bot' &&
					Array.isArray(entry.sources) &&
					entry.sources.length
				) {
					const $last = $('#chatbot-messages .bot-message').last();
					renderSources($last, entry.sources);
				}
			});
			if (saved.isOpen) {
				openChatbot();
			}
		} else {
			// Load greeting only for a fresh session
			loadGreeting();
		}
	}

	/**
   * Attach event handlers
   */
	function attachEventHandlers() {
		// Toggle button
		$('#chatbot-toggle').on('click', toggleChatbot);

		// Close button
		$('.chatbot-close').on('click', closeChatbot);

		// Minimize button
		$('.chatbot-minimize').on('click', closeChatbot);

		$('.chatbot-clear').on('click', function () {
			clearPersistedConversation();
			loadGreeting();
			persistConversation();
		});

		// Send button
		$('#chatbot-send').on('click', sendMessage);

		// Enter key in textarea
		$('#chatbot-input').on('keydown', function (e) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				sendMessage();
			}
		});

		// Auto-resize textarea
		$('#chatbot-input').on('input', function () {
			this.style.cssText = 'height: auto';
			this.style.cssText = `height: ${Math.min(this.scrollHeight, 120)}px`;
		});
	}

	/**
   * Toggle chatbot window
   */
	function toggleChatbot() {
		if (isOpen) {
			closeChatbot();
		} else {
			openChatbot();
		}
	}

	/**
   * Open chatbot window
   */
	function openChatbot() {
		$('#chatbot-window').addClass('chatbot-visible');
		$('#chatbot-toggle').addClass('open');
		isOpen = true;
		persistConversation();

		// Focus input
		setTimeout(function () {
			$('#chatbot-input').focus();
		}, 300);
	}

	/**
   * Close chatbot window
   */
	function closeChatbot() {
		$('#chatbot-window').removeClass('chatbot-visible');
		$('#chatbot-toggle').removeClass('open');
		isOpen = false;
		persistConversation();
	}

	/**
   * Load greeting
   */
	function loadGreeting() {
		$.ajax({
			url: democraticChatbot.ajaxUrl,
			type: 'POST',
			data: {
				action: 'democratic_chatbot_greeting',
				nonce: democraticChatbot.nonce,
			},
			success(response) {
				if (response.success && response.data.message) {
					addMessage(response.data.message, 'bot');
					persistConversation();
				}
			},
		});
	}

	/**
   * Send message
   */
	function sendMessage() {
		const $input = $('#chatbot-input');
		const message = $input.val().trim();

		if (!message) {
			return;
		}

		// Add user message to chat
		addMessage(message, 'user');

		// Clear input
		$input.val('');
		$input[0].style.cssText = 'height: auto';

		// Show typing indicator
		showTypingIndicator();

		if (isGreeting(message)) {
			hideTypingIndicator();
			loadGreeting();
			return;
		}

		if (!democraticChatbot.streamUrl) {
			hideTypingIndicator();
			addMessage(democraticChatbot.i18n.error, 'bot', true);
			return;
		}

		sendStreamMessage(message);
	}

	function isGreeting(message) {
		const text = String(message || '')
			.trim()
			.toLowerCase();
		if (!text) {
			return false;
		}
		return /^(hi|hello|hey|good\s(morning|afternoon|evening)|hiya|howdy)(\b|!|\.|\?)$/i.test(
			text,
		);
	}

	/**
   * Send message using streaming API Gateway endpoint.
   *
   * @param {string} message User message
   */
	async function sendStreamMessage(message) {
		try {
			const contextPayload = await fetchAssistantContext(message);
			if (contextPayload && contextPayload.blockedMessage) {
				hideTypingIndicator();
				addMessage(contextPayload.blockedMessage, 'bot');
				conversationHistory.push({
					user: message,
					bot: contextPayload.blockedMessage,
				});
				persistConversation();
				return;
			}

			const token = await fetchStreamToken();
			if (!token) {
				hideTypingIndicator();
				addMessage(democraticChatbot.i18n.error, 'bot', true);
				return;
			}

			const headers = {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			};

			if (window.location && window.location.host) {
				headers['x-tenant-origin'] = window.location.host;
			}

			const response = await fetch(democraticChatbot.streamUrl, {
				method: 'POST',
				headers,
				body: JSON.stringify({
					query: message,
					conversationHistory: formatConversationHistory(),
					platformContext: (contextPayload && contextPayload.platformContext) || '',
					citations: (contextPayload && contextPayload.citations) || [],
				}),
			});

			if (!response.ok || !response.body) {
				throw new Error('stream_failed');
			}

			hideTypingIndicator();

			const $streamMessage = addStreamingMessage();
			const $content = $streamMessage.find('.message-content');
			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let botText = '';

			for (;;) {
				const { value, done } = await reader.read();
				if (value) {
					botText += decoder.decode(value, { stream: !done });
				}
				if (done) {
					botText += decoder.decode();
					break;
				}
				const parsed = splitCitationsTrailer(botText);
				$content.html(formatMessageText(parsed.text));
				scrollMessagesToBottom();
			}

			const finalParsed = splitCitationsTrailer(botText);
			$content.html(formatMessageText(finalParsed.text));
			if (
				finalParsed.citations.length &&
				!isOutOfScopeBotText(finalParsed.text)
			) {
				renderSources($streamMessage, finalParsed.citations);
			}

			conversationHistory.push({
				user: message,
				bot: finalParsed.text,
			});
			displayMessages.push({
				text: finalParsed.text,
				sender: 'bot',
				isError: false,
				sources:
					finalParsed.citations.length && !isOutOfScopeBotText(finalParsed.text)
						? finalParsed.citations
						: [],
			});
			persistConversation();
		} catch (error) {
			hideTypingIndicator();
			addMessage(democraticChatbot.i18n.error, 'bot', true);
			persistConversation();
		}
	}

	/**
	 * Fetch RBAC-filtered platform context from WordPress.
	 *
	 * @param {string} message User message
	 * @return {Promise<{platformContext: string, citations: Array, blockedMessage: string}|null>}
	 */
	function fetchAssistantContext(message) {
		return new Promise((resolve) => {
			$.ajax({
				url: democraticChatbot.ajaxUrl,
				type: 'POST',
				data: {
					action: 'democratic_chatbot_context',
					nonce: democraticChatbot.nonce,
					message,
					history: JSON.stringify(conversationHistory.slice(-8)),
				},
				success(response) {
					if (response.success && response.data) {
						resolve(response.data);
						return;
					}
					resolve(null);
				},
				error() {
					resolve(null);
				},
			});
		});
	}

	/**
	 * Split streamed answer from citation trailer.
	 *
	 * @param {string} raw
	 * @return {{ text: string, citations: Array<{title: string, url: string}> }}
	 */
	function splitCitationsTrailer(raw) {
		let text = String(raw || '');
		// Defence in depth: strip any citation blocks even if markers are partial/duplicated.
		const blockRe = /<<<CITATIONS>>>[\s\S]*?(?:<<<END_CITATIONS>>>|$)/g;
		const blocks = text.match(blockRe) || [];
		let citations = [];

		blocks.forEach((block) => {
			const start = block.indexOf('<<<CITATIONS>>>');
			const end = block.indexOf('<<<END_CITATIONS>>>');
			const jsonPart =
				end === -1
					? block.slice(start + '<<<CITATIONS>>>'.length).trim()
					: block.slice(start + '<<<CITATIONS>>>'.length, end).trim();
			try {
				const parsed = JSON.parse(jsonPart);
				if (Array.isArray(parsed)) {
					citations = citations.concat(
						parsed.filter((c) => c && (c.title || c.url)),
					);
				}
			} catch (e) {
				// Incomplete stream chunk — ignore until trailer is complete.
			}
		});

		text = text.replace(blockRe, '').trim();
		// Strip orphan markers if the closing tag never arrived.
		text = text
			.replace(/<<<CITATIONS>>>[\s\S]*$/g, '')
			.replace(/<<<END_CITATIONS>>>/g, '')
			.trim();

		return { text, citations };
	}

	function isOutOfScopeBotText(text) {
		const lower = String(text || '').toLowerCase();
		return (
			lower.includes("couldn't find that in my knowledge base") ||
			lower.includes('only answer questions about council services') ||
			lower.includes("i don't have that information")
		);
	}

	/**
	 * Render source links under a bot message.
	 *
	 * @param {jQuery} $message
	 * @param {Array<{title: string, url: string}>} citations
	 */
	function renderSources($message, citations) {
		if (!$message || !citations || !citations.length) {
			return;
		}
		const usable = citations.filter((c) => {
			const url = String((c && c.url) || '').trim();
			return c && c.title && !/^s3:\/\//i.test(url);
		});
		if (!usable.length) {
			return;
		}
		$message.find('.message-sources').remove();
		const label = (democraticChatbot.i18n && democraticChatbot.i18n.sources) || 'Sources';
		const items = usable
			.map((c) => {
				const title = escapeHtml(c.title || 'Source');
				const url = String(c.url || '').trim();
				if (url && (/^https?:\/\//i.test(url) || url.startsWith('/'))) {
					const href = url.startsWith('/')
						? `${window.location.origin}${url}`
						: url;
					return `<li><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${title}</a></li>`;
				}
				return `<li>${title}</li>`;
			})
			.join('');
		$message.append(
			`<div class="message-sources"><div class="message-sources-label">${escapeHtml(label)}</div><ul>${items}</ul></div>`,
		);
		scrollMessagesToBottom();
	}

	/**
	 * Fetch a short-lived streaming token from WordPress.
   *
   * @return {Promise<string>}
   */
	function fetchStreamToken() {
		return new Promise((resolve) => {
			$.ajax({
				url: democraticChatbot.ajaxUrl,
				type: 'POST',
				data: {
					action: 'democratic_chatbot_token',
					nonce: democraticChatbot.nonce,
				},
				success(response) {
					if (response.success && response.data.token) {
						resolve(response.data.token);
						return;
					}

					resolve('');
				},
				error() {
					resolve('');
				},
			});
		});
	}

	/**
   * Convert local history into Bedrock-compatible message format.
   *
   * @return {Array}
   */
	function formatConversationHistory() {
		const history = [];

		conversationHistory.forEach((entry) => {
			if (entry.user) {
				history.push({
					role: 'user',
					content: [{ text: entry.user }],
				});
			}

			if (entry.bot) {
				history.push({
					role: 'assistant',
					content: [{ text: entry.bot }],
				});
			}
		});

		return history;
	}

	/**
	 * Add message to chat
	 *
	 * @param {string}  text       Message text
	 * @param {string}  sender     Message sender (user/bot)
	 * @param {boolean} isError    Whether message is an error
	 * @param {boolean} trackDisplay Whether to append to displayMessages (default true)
	 */
	function addMessage(text, sender, isError = false, trackDisplay = true) {
		const $messages = $('#chatbot-messages');
		const timestamp = new Date().toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
		});
		const formattedText = formatMessageText(text);

		const messageHtml = `
			<div class="chatbot-message ${sender}-message ${isError ? 'error-message' : ''}">
				<div class="message-content">${formattedText}</div>
				<div class="message-timestamp">${timestamp}</div>
			</div>
		`;

		$messages.append(messageHtml);

		if (trackDisplay) {
			displayMessages.push({
				text: String(text || ''),
				sender,
				isError: !!isError,
				sources: [],
			});
			persistConversation();
		}

		// Scroll to bottom
		scrollMessagesToBottom();
	}

	/**
   * Add a streaming bot message container.
   *
   * @return {jQuery} The message element
   */
	function addStreamingMessage() {
		const $messages = $('#chatbot-messages');
		const timestamp = new Date().toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
		});

		const messageHtml = `
			<div class="chatbot-message bot-message">
				<div class="message-content"></div>
				<div class="message-timestamp">${timestamp}</div>
			</div>
		`;

		const $message = $(messageHtml);
		$messages.append($message);
		scrollMessagesToBottom();

		return $message;
	}

	/**
   * Show typing indicator
   */
	function showTypingIndicator() {
		const $messages = $('#chatbot-messages');
		const indicatorHtml = `
			<div class="chatbot-message bot-message typing-indicator" id="typing-indicator">
				<div class="message-content">
					<span class="typing-dot"></span>
					<span class="typing-dot"></span>
					<span class="typing-dot"></span>
				</div>
			</div>
		`;

		$messages.append(indicatorHtml);
		scrollMessagesToBottom();
	}

	/**
   * Hide typing indicator
   */
	function hideTypingIndicator() {
		$('#typing-indicator').remove();
	}

	function scrollMessagesToBottom() {
		const $messages = $('#chatbot-messages');
		if ($messages.length) {
			$messages.scrollTop($messages[0].scrollHeight);
		}
	}

	/**
   * Escape HTML
   *
   * @param {string} text Text to escape
   * @return {string} Escaped text
   */
	function escapeHtml(text) {
		const map = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#039;',
		};
		return text.replace(/[&<>"']/g, (m) => map[m]);
	}

	function formatMessageText(text) {
		// Never render citation trailers inside the bubble (defence in depth).
		const cleaned = String(text || '')
			.replace(/<<<CITATIONS>>>[\s\S]*?(?:<<<END_CITATIONS>>>|$)/g, '')
			.replace(/<<<CITATIONS>>>[\s\S]*$/g, '')
			.replace(/<<<END_CITATIONS>>>/g, '')
			.trim();
		const normalizedLines = normalizeMessageFormatting(cleaned);
		const formatted = normalizedLines.map((line) => {
			if (line.type === 'heading') {
				return `<strong>${escapeHtml(line.text)}</strong>`;
			}
			return escapeHtml(line.text);
		});
		return formatted.join('<br>');
	}

	function normalizeMessageFormatting(text) {
		const lines = String(text || '')
			.replace(/\r\n/g, '\n')
			.split('\n');
		const normalized = lines.map((line) => {
			const headingMatch = line.match(/^\s*-\s*\*\*(.+?)\*\*\s*:?\s*$/);
			if (headingMatch) {
				return {
					type: 'heading',
					text: `${headingMatch[1]}:`,
				};
			}

			const cleaned = line
				.replace(/\*\*(.*?)\*\*/g, '$1')
				.replace(/__(.*?)__/g, '$1')
				.replace(/(\*\*|__)/g, '')
				.replace(/^\s*-\s+/, '\u2022 ');

			return {
				type: 'text',
				text: cleaned,
			};
		});

		return normalized;
	}

	// Initialize on document ready
	$(document).ready(function () {
		initChatbot();
	});
})(jQuery);
